import { useEffect, useMemo, useState } from "react";
import { useSortBy, useTable } from 'react-table';
import { Priorities, searchTermKey } from "../../../../../Constants/Consts";
import { IoIosCamera } from "react-icons/io";
import { PunchlistApiType } from "../../../../../types/PunchList/PunchListSchema";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "../../../../../Helpers/Functions";

export const useTableData = (punchlists: PunchlistApiType[], key?: string) => {
    const { Categories } = useSelector((state: RootState) => state.Categories)
    const { Issues } = useSelector((state: RootState) => state.Issues)

    const data = useMemo(() => {
        return punchlists.map(p => {
            const dueDateParts = p.dueDate ? p.dueDate.split('-').map(part => parseInt(part, 10)) : [];
            let formattedDueDate = '';
            if (dueDateParts.length === 3) {
                const [year, month, day] = dueDateParts;
                formattedDueDate = new Date(year, month - 1, day).toLocaleDateString();
            }
            return {
                ...p,
                DueDate: formattedDueDate,
                DateIdentified: p.dateIdentified ? new Date(p.dateIdentified).toLocaleDateString() : '',
                mpctag: p.punchListTag || p.punchListUuid?.slice(0, 8)?.toLocaleUpperCase(),
                priority: Object.entries(Priorities).find(([_, v]) => v == p.priority)?.[0],
                category: Categories.find(c => c.categoryId == p.categoryId)?.categoryDescription || p.categoryV1Text,
                issue: Issues.find(issue => issue.issueId == p.issueId)?.issueDescription || p.issueV1Text
            };
        });
    }, [punchlists, Categories, Issues]);

    const searchTermLocalStorageKey = key ? `${key}_${searchTermKey}` : searchTermKey;
    const sortingLocalStorageKey = key ? `${key}_${searchTermKey}_sorting` : `${searchTermKey}_sorting`;

    const [search, setSearch] = useState(() => {
        const cashed = getLocalStorageItem<string>(searchTermLocalStorageKey);
        return cashed ?? "";
    });

    const [sortBy, setSortBy] = useState(() => {
        const cashed = getLocalStorageItem(sortingLocalStorageKey);
        return cashed ?? []
    })


    const handleUpdateSearchTermInLocalStorage = (value = search) => setLocalStorageItem(searchTermLocalStorageKey, value)
    const handleClearSearchTermInLocalStorage = () => { removeLocalStorageItem(searchTermLocalStorageKey); removeLocalStorageItem(sortingLocalStorageKey); }
    const handleUpdateSorting = (value) => { setSortBy(value); setLocalStorageItem(sortingLocalStorageKey, (value)) }

    const filtered = useMemo(() => {
        const trimmedSearch = search?.trim();
        if (!trimmedSearch) return data;
        return data.filter(d => JSON.stringify(d).toLocaleLowerCase().match(trimmedSearch.toLocaleLowerCase()))
    }, [data, search]);

    const columns = useMemo(() => [
        {
            Header: "",
            accessor: 'hasImage',
            disableSortBy: true,
            Cell: ({ row }) => {
                const hasDefectImage = row.original.defectPic1 || row.original.defectPic2;
                return hasDefectImage ? <IoIosCamera className="text-lg" /> : null;
            }
        },
        {
            Header: "Sequence / CA",
            accessor: 'adhocSequenceCa',
        },
        {
            Header: "Unit / Device",
            accessor: 'adhocUnitDevice',
        },
        {
            Header: "Category",
            accessor: 'category',
        },
        {
            Header: "Issue",
            accessor: 'issue',
        },
        {
            Header: "Drawing #",
            accessor: 'drawingNumber',
            sortable: true
        },
        {
            Header: "Identified By",
            accessor: 'identifiedBy',
        },
        {
            Header: "Priority",
            accessor: 'priority',
        },
        {
            Header: "Due Date",
            accessor: 'DueDate',
            sortType: (rowA, rowB) => {
                const a = rowA.original.dueDate || '';
                const b = rowB.original.dueDate || '';
                return a.localeCompare(b);
            },
        },
        {
            Header: "Date Identified",
            accessor: 'DateIdentified',
            sortType: (rowA, rowB) => {
                const a = rowA.original.dateIdentified || '';
                const b = rowB.original.dateIdentified || '';
                return a.localeCompare(b);
            },
        },
        {
            Header: "Punch List Tag",
            accessor: 'mpctag',
        },
        // {
        //     Header: "Sequence #",
        //     accessor: '',
        // },
        // {
        //     Header: "Unit #",
        //     accessor: '',
        // },
    ], []);

    const tableInstance = useTable({
        columns,
        data: filtered,
        autoResetFilters: false,
        autoResetSortBy: false,
        initialState: { sortBy }
    }, useSortBy)

    useEffect(() => {
        if (tableInstance.state.sortBy.length > 0) handleUpdateSorting(tableInstance.state.sortBy)
    }, [tableInstance.state.sortBy])

    return {
        ...tableInstance,
        search,
        setSearch,
        handleUpdateSearchTermInLocalStorage,
        handleClearSearchTermInLocalStorage
    }
}
